import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { PublicPreboardingFormField } from '@hygge/shared';
import { Upload } from 'lucide-react';
import { useId } from 'react';

interface PublicFieldRendererProps {
  field: PublicPreboardingFormField;
  value: string | boolean;
  onChange: (value: string | boolean) => void;
  file?: File | null;
  onFileChange?: (file: File | null) => void;
}

// Renders one candidate-fillable input, driven by the server-resolved
// `inputType`. Mirrors the career-site field renderer. File bytes are held in
// the page's local state only — uploading happens in HW-587.
export function PublicFieldRenderer({
  field,
  value,
  onChange,
  file,
  onFileChange,
}: PublicFieldRendererProps) {
  const inputId = useId();
  const label = (
    <>
      {field.label} {field.required && <span className="text-destructive">*</span>}
    </>
  );

  if (field.inputType === 'FILE') {
    return (
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-7 text-center cursor-pointer transition-all duration-200',
            'hover:border-hygge/50 hover:bg-hygge/5',
            file && 'border-hygge/30 bg-hygge/5',
          )}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <input
            id={inputId}
            type="file"
            className="hidden"
            onChange={(e) => onFileChange?.(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <div className="flex items-center justify-center gap-2.5 min-w-0">
              <Upload className="h-4 w-4 text-hygge flex-shrink-0" />
              <span className="text-sm font-medium truncate min-w-0" title={file.name}>
                {file.name}
              </span>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-destructive transition-colors ml-1 flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onFileChange?.(null);
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Upload className="h-9 w-9 mx-auto text-muted-foreground" />
              <p className="text-[13.5px] text-muted-foreground">
                Drop file here or <span className="text-hygge font-semibold">browse</span>
              </p>
            </div>
          )}
        </div>
        {field.helperText && <FieldHint text={field.helperText} />}
      </div>
    );
  }

  if (field.inputType === 'LONG_TEXT') {
    return (
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        <textarea
          className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-[13.5px] shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-hygge focus-visible:border-hygge transition-colors"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
        />
        {field.helperText && <FieldHint text={field.helperText} />}
      </div>
    );
  }

  if (field.inputType === 'CHECKBOX') {
    return (
      <div className="space-y-1">
        <div className="flex items-start gap-2.5">
          <Checkbox
            id={inputId}
            checked={value === true}
            onCheckedChange={(c) => onChange(c === true)}
            className="mt-0.5"
          />
          <label
            htmlFor={inputId}
            className="text-[13px] text-muted-foreground leading-relaxed cursor-pointer"
          >
            {label}
          </label>
        </div>
        {field.helperText && <FieldHint text={field.helperText} />}
      </div>
    );
  }

  if (field.inputType === 'DROPDOWN') {
    return (
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        <Select value={typeof value === 'string' ? value : ''} onValueChange={onChange}>
          <SelectTrigger className="h-9 text-[13.5px] focus:ring-hygge">
            <SelectValue placeholder="Select an option..." />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {field.helperText && <FieldHint text={field.helperText} />}
      </div>
    );
  }

  const htmlType =
    field.inputType === 'EMAIL'
      ? 'email'
      : field.inputType === 'PHONE'
        ? 'tel'
        : field.inputType === 'NUMBER'
          ? 'number'
          : field.inputType === 'URL'
            ? 'url'
            : field.inputType === 'DATE'
              ? 'date'
              : 'text';

  const numberInvalid =
    field.inputType === 'NUMBER' &&
    typeof value === 'string' &&
    value.trim() !== '' &&
    (() => {
      const n = Number(value);
      return !Number.isFinite(n) || n < 0;
    })();

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <Input
        type={htmlType}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
        min={field.inputType === 'NUMBER' ? 0 : undefined}
        step={field.inputType === 'NUMBER' ? 1 : undefined}
        aria-invalid={numberInvalid || undefined}
        className={cn(
          'text-[13.5px] focus-visible:ring-hygge focus-visible:border-hygge',
          numberInvalid &&
            'border-destructive focus-visible:ring-destructive focus-visible:border-destructive',
        )}
      />
      {numberInvalid ? (
        <p className="text-xs text-destructive">Must be a non-negative number.</p>
      ) : (
        field.helperText && <FieldHint text={field.helperText} />
      )}
    </div>
  );
}

function FieldHint({ text }: { text: string }) {
  return <p className="text-xs text-muted-foreground/70">{text}</p>;
}
